import type { CartItem } from "@/types"

/** Idle time with a non-empty bag before the reminder enrolment. */
export const ABANDONED_CART_AFTER_MS = 30 * 60 * 1000
const PREFIX = "meucontroleai:abandoned-cart:"

/** Session timer: when the bag first sat idle, and whether the enrolment already went out. */
export type AbandonedCartTimer = { startedAt: number; sent: boolean }

function key(merchantId: string): string {
  return PREFIX + merchantId
}

/** The timer persisted for this tab session, if any. */
export function readAbandonedCartTimer(merchantId: string): AbandonedCartTimer | null {
  try {
    const raw = sessionStorage.getItem(key(merchantId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return null
    const { startedAt, sent } = parsed as Record<string, unknown>
    if (typeof startedAt !== "number" || !Number.isFinite(startedAt)) return null
    return { startedAt, sent: sent === true }
  } catch {
    return null
  }
}

/** Persists the timer. */
export function writeAbandonedCartTimer(merchantId: string, timer: AbandonedCartTimer): void {
  try {
    sessionStorage.setItem(key(merchantId), JSON.stringify(timer))
  } catch {
    return
  }
}

/** Cancels the timer (order placed or bag emptied) unless the enrolment already went out. */
export function cancelAbandonedCartTimer(merchantId: string): void {
  try {
    if (readAbandonedCartTimer(merchantId)?.sent) return
    sessionStorage.removeItem(key(merchantId))
  } catch {
    return
  }
}

/** Milliseconds until the enrolment is due; null when it was already sent. */
export function abandonedCartDelayMs(timer: AbandonedCartTimer, now: number): number | null {
  if (timer.sent) return null
  return Math.max(0, timer.startedAt + ABANDONED_CART_AFTER_MS - now)
}

/** Name of the line with the most units, first one on a tie. */
export function favoriteItemName(items: readonly CartItem[]): string {
  let best: CartItem | null = null
  for (const line of items) if (!best || line.quantity > best.quantity) best = line
  return best?.item.name ?? ""
}
